import * as React from 'react';

import { useApolloClient } from '@apollo/client/react';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { SidebarChildLink } from '@app/components/sidebar/SidebarChildLink';
import { SidebarGroupPopover } from '@app/components/sidebar/SidebarGroupPopover';
import { type GroupHeaderConfig } from '@app/components/sidebar/sidebarLinkConfig';
import {
  SIDEBAR_ICON_SIZE,
  isPathActive,
  navItemSx,
} from '@app/components/sidebar/sidebarNavStyles';

type Props = {
  link: GroupHeaderConfig;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
};

export const SidebarNavGroup = ({ link, collapsed, open, onToggle }: Props) => {
  const client = useApolloClient();
  const location = useLocation();
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null>(
    null,
  );
  const { prefetch, icon: Icon } = link;

  // The current route lives inside this group (own path or any child path)
  const groupActive =
    isPathActive(link.path, location.pathname) ||
    link.children.some(child => isPathActive(child.path, location.pathname));

  const prefetchOnHover = prefetch ? () => client.query(prefetch) : undefined;

  if (collapsed) {
    return (
      <>
        <Tooltip
          // hidden while the popover menu is open
          title={popoverAnchor == null ? link.label : ''}
          placement="right"
          arrow
          disableInteractive
        >
          <ListItemButton
            selected={groupActive}
            disableRipple={true}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              // keep the mobile drawer open for group toggles
              event.stopPropagation();
              setPopoverAnchor(event.currentTarget);
            }}
            onMouseEnter={prefetchOnHover}
            sx={[
              navItemSx(true),
              // while the popover menu is open keep the hover-style gray so
              // the icon reads as the parent of the menu; blue is reserved
              // for the active route
              popoverAnchor != null &&
                (theme => ({
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  ...theme.applyStyles('dark', {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }),
                })),
            ]}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 1.5,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <Icon size={SIDEBAR_ICON_SIZE} />
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
        <SidebarGroupPopover
          link={link}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
        />
      </>
    );
  }

  return (
    <>
      <ListItemButton
        selected={groupActive && !open}
        disableRipple={true}
        onClick={event => {
          // keep the mobile drawer open for group toggles
          event.stopPropagation();
          onToggle();
        }}
        onMouseEnter={prefetchOnHover}
        sx={navItemSx(false)}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: 1.5,
            justifyContent: 'center',
            color: 'inherit',
          }}
        >
          <Icon size={SIDEBAR_ICON_SIZE} />
        </ListItemIcon>
        <ListItemText
          primary={link.label}
          slotProps={{
            primary: { sx: { fontSize: 14, fontWeight: 500 }, noWrap: true },
          }}
        />
        {open ? (
          <ChevronUpIcon style={{ opacity: 0.7 }} size={20} />
        ) : (
          <ChevronDownIcon style={{ opacity: 0.7 }} size={20} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding>
          {link.children
            .filter(child => child.visible)
            .map(child => (
              <SidebarChildLink
                key={child.key}
                link={child}
                variant="accordion"
              />
            ))}
        </List>
      </Collapse>
    </>
  );
};
